import React from 'react';
import { Box, Avatar, Typography, Slider, Chip } from '@mui/material';
import { Player } from '../types';

interface PlayerCardProps {
  player: Player;
  isCurrentPlayer: boolean;
  isAdminViewing: boolean;
  onCoefficientChange?: (playerId: string, value: number) => void;
}

const PlayerCard: React.FC<PlayerCardProps> = ({
  player,
  isCurrentPlayer,
  isAdminViewing,
  onCoefficientChange,
}) => {
  return (
    <Box
      sx={{
        background: 'white',
        border: `3px solid black`,
        borderRadius: '15px',
        boxShadow: '0 4px 0 black, 0 6px 12px rgba(0,0,0,0.2)',
        padding: '12px',
        marginBottom: '12px',
        opacity: player.isConnected ? 1 : 0.5,
        transition: 'opacity 0.3s',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '8px',
      }}
    >
      {/* Avatar */}
      {player.avatarType === 'custom' ? (
        <Avatar
          src={player.avatar}
          sx={{ width: 60, height: 60, border: `3px solid ${player.color}` }}
        />
      ) : (
        <Box
          sx={{
            width: 60, height: 60, borderRadius: '50%',
            border: `3px solid ${player.color}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '2rem', background: 'rgba(0,0,0,0.05)',
          }}
        >
          {player.avatar || '?'}
        </Box>
      )}

      {/* Name row */}
      <Box display="flex" alignItems="center" gap="4px" flexWrap="wrap" justifyContent="center">
        {player.isAdmin && (
          <Typography component="span" sx={{ fontSize: '1rem' }}>👑</Typography>
        )}
        <Typography
          sx={{
            fontFamily: '"Comic Sans MS", cursive, sans-serif',
            fontWeight: 'bold', fontSize: '0.95rem',
            color: player.color, textAlign: 'center', wordBreak: 'break-word',
          }}
        >
          {player.name || '...'}
        </Typography>
        {isCurrentPlayer && (
          <Chip
            label="You"
            size="small"
            sx={{
              fontSize: '0.65rem', height: '18px',
              background: player.color, color: 'white',
              fontFamily: '"Comic Sans MS", cursive, sans-serif', fontWeight: 'bold',
              animation: 'youBob 2s ease-in-out infinite',
              '@keyframes youBob': {
                '0%, 100%': { transform: 'translateY(0)' },
                '50%': { transform: 'translateY(-5px)' },
              },
            }}
          />
        )}
      </Box>

      {/* Disconnected indicator */}
      {!player.isConnected && (
        <Typography variant="caption" sx={{ color: '#999' }}>...</Typography>
      )}

      {/* Coefficient */}
      {isAdminViewing && onCoefficientChange ? (
        <Box sx={{ width: '100%', px: 1 }}>
          <Typography
            variant="caption"
            sx={{
              fontFamily: '"Comic Sans MS", cursive, sans-serif',
              fontWeight: 'bold', display: 'block', textAlign: 'center', color: '#333',
            }}
          >
            x{player.coefficient.toFixed(1)}
          </Typography>
          <Slider
            min={0.1} max={10} step={0.1}
            value={player.coefficient}
            onChange={(_e, val) => onCoefficientChange(player.id, val as number)}
            size="small"
            sx={{ color: player.color, '& .MuiSlider-thumb': { width: 14, height: 14 } }}
          />
        </Box>
      ) : (
        <Chip
          label={`x${player.coefficient.toFixed(1)}`}
          size="small"
          sx={{
            fontFamily: '"Comic Sans MS", cursive, sans-serif', fontWeight: 'bold',
            background: player.color, color: 'white', fontSize: '0.75rem',
          }}
        />
      )}
    </Box>
  );
};

export default PlayerCard;
